import { action, observable, makeObservable } from 'mobx';
import { BOX_REFRESH_INTERVAL, BOX_REFRESH_MAX_AGE } from 'constants/BoxConstants';

export interface IBox {
    accountCount: number;
    applicationCount: number;
    artifactCount: number;
    autostartCount: number;
    connectionCount: number;
    data: {
        counts: {
            compromised: {
                accounts: number;
                artifacts: number;
                autostarts: number;
                drivers: number;
                files: number;
                hosts: number;
                memory: number;
                modules: number;
                processes: number;
                scripts: number;
            };
            total: {
                accounts: number;
                artifacts: number;
                autostarts: number;
                drivers: number;
                files: number;
                hosts: number;
                memory: number;
                modules: number;
                processes: number;
                scripts: number;
                totalHosts: number;
            };
        };
    };
    driverCount: number;
    filterOn: Date;
    hookCount: null;
    hostCount: number;
    id: string;
    lastScannedOn: Date;
    memoryCount: number;
    moduleCount: number;
    name: string;
    processCount: number;
    refreshOn: null | Date;
    createdOn: null | Date;
    refreshedOn: null | Date;
    refreshDelta?: number;
    refreshing?: boolean;
    refreshDisabled?: boolean;
    disabled?: boolean;
    scriptCount: number;
    targetId: null | string;
    timeWindow: null | {
        days: number;
    };
    totalHostCount: number;
    createdBy?: string;
    filter?: {
        startDate: string;
        endDate: string;
        flag: any[];
        threat: any[];
        scanOptions: any[];
    };
    isBox: boolean;
    customBox: boolean;
}

export default class BoxStore {
    box: IBox = {
        accountCount: 0,
        applicationCount: 0,
        artifactCount: 0,
        autostartCount: 0,
        connectionCount: 0,
        data: {
            counts: {
                compromised: {
                    accounts: 0,
                    artifacts: 0,
                    autostarts: 0,
                    drivers: 0,
                    files: 0,
                    hosts: 0,
                    memory: 0,
                    modules: 0,
                    processes: 0,
                    scripts: 0
                },
                total: {
                    accounts: 0,
                    artifacts: 0,
                    autostarts: 0,
                    drivers: 0,
                    files: 0,
                    hosts: 0,
                    memory: 0,
                    modules: 0,
                    processes: 0,
                    scripts: 0,
                    totalHosts: 0
                }
            }
        },
        driverCount: 0,
        filterOn: new Date(),
        hookCount: null,
        hostCount: 0,
        id: '',
        lastScannedOn: new Date(),
        memoryCount: 0,
        moduleCount: 0,
        name: '',
        processCount: 0,
        refreshOn: null,
        createdOn: new Date(),
        refreshedOn: new Date(),
        scriptCount: 0,
        targetId: null,
        timeWindow: null,
        totalHostCount: 0,
        createdBy: '',
        filter: {
            startDate: '',
            endDate: '',
            flag: [],
            threat: [],
            scanOptions: []
        },
        isBox: true,
        customBox: false
    };

    constructor(box: IBox) {
        makeObservable(this, {
            box: observable,
            update: action
        });

        this.update(box);
    }

    update(box: IBox): void {
        const refreshOn = box.refreshOn ? new Date(box.refreshOn) : null;
        const createdOn = box.createdOn ? new Date(box.createdOn) : null;
        const refreshedOn = box.refreshedOn ? new Date(box.refreshedOn) : null;
        const refreshDelta = refreshedOn ? Date.now() - refreshedOn.getTime() : BOX_REFRESH_INTERVAL;
        const refreshing = (!!refreshOn && !refreshedOn) || (!!refreshOn && !!refreshedOn && refreshOn > refreshedOn);
        const refreshDisabled = !box.lastScannedOn || refreshing || refreshDelta < BOX_REFRESH_INTERVAL;
        const customBox = !box.createdBy;

        const disabled = box.createdBy ? !refreshedOn : !refreshedOn || refreshDelta > BOX_REFRESH_MAX_AGE;
        this.box = {
            ...this.box,
            ...box,
            refreshOn,
            createdOn,
            refreshedOn,
            refreshDelta,
            refreshing,
            refreshDisabled,
            disabled,
            customBox
        };
    }
}
